import {
  isCandidateAiProcessingCutoffBlocked,
  isCandidateCvProcessingActive,
} from '../candidate-ai/candidateAiReviewState';
import { getCandidateAiReviewCvHash } from '../candidate-ai/useCandidateAiReviewActions';
import { CandidateType } from '../candidates/Candidates.types';

type CvSuggestionsNotice = {
  type: 'info' | 'warning';
  message: string;
  description?: string;
  offerConsents?: boolean;
};

const unreadableCv = 'The file may be broken, invalid, or an image scan.';

const previousCvReasons: Record<string, string> = {
  'current-cv-processing': 'The current CV is being processed.',
  'current-cv-processing-blocked': "The current CV can't be read until Data Processing and Offers consent is active.",
  'current-cv-processing-failed': `The current CV couldn't be read. ${unreadableCv}`,
  'current-cv-not-processed': "The current CV hasn't been read yet.",
  'missing-cv': 'No current CV is attached.',
};

export const CV_READING_CUTOFF_MESSAGE =
  'Reading CVs is unavailable because this candidate was created before the cutoff date.';

export const getCvSuggestionsState = (candidate: CandidateType, hasSuggestions: boolean) => {
  const { cvProcessing, aiSuggestions } = candidate;
  const isCutoffBlocked = isCandidateAiProcessingCutoffBlocked(cvProcessing);
  const isMissingCv = !candidate.cv || cvProcessing.blockedReason === 'missing-cv';
  const isPreviousCv = hasSuggestions && aiSuggestions.basedOnCurrentCv === false;
  const isConsentBlocked =
    !isMissingCv &&
    cvProcessing.canProcess === false &&
    cvProcessing.blockedReason === 'missing-data-processing-consent';
  const isMissingHash = !getCandidateAiReviewCvHash(candidate);
  const isReadOnly = isMissingCv || isPreviousCv || isMissingHash || (isConsentBlocked && !hasSuggestions);

  // One notice per page, matching the matcher's rule of combining overlapping warnings.
  const notice = ((): CvSuggestionsNotice | null => {
    if (isPreviousCv) {
      return {
        type: 'warning',
        message: 'Suggestions from a previous CV',
        description: `${
          (aiSuggestions.staleReason && previousCvReasons[aiSuggestions.staleReason]) || ''
        } These suggestions are read-only.`.trim(),
        offerConsents: isConsentBlocked,
      };
    }
    if (isCutoffBlocked) return { type: 'info', message: CV_READING_CUTOFF_MESSAGE };
    if (isMissingCv) {
      return {
        type: 'info',
        message: hasSuggestions
          ? 'No CV is attached. Attach a CV to review these suggestions.'
          : 'Add a CV before CV suggestions can be generated.',
      };
    }
    if (isConsentBlocked) {
      return {
        type: 'warning',
        message: 'Reading new CVs is paused until Data Processing and Offers consent is active.',
        description: hasSuggestions
          ? 'Suggestions from the current CV can still be reviewed.'
          : 'Once the consent is approved or renewed, the attached CV is read automatically.',
        offerConsents: true,
      };
    }
    if (cvProcessing.status === 'FAILED_NON_RETRYABLE') {
      return { type: 'warning', message: "CV couldn't be read", description: unreadableCv };
    }
    if (isCandidateCvProcessingActive(cvProcessing.status)) {
      return { type: 'info', message: 'Processing CV…', description: 'Suggestions may change when it finishes.' };
    }
    if (isMissingHash && hasSuggestions) {
      return { type: 'info', message: 'Reviewing is temporarily unavailable because the CV review hash is missing.' };
    }
    return null;
  })();

  return { notice, isReadOnly, isRead: cvProcessing.status === 'READY' };
};
